import { ResearchDelivery, type ResearchAttempt } from './research/delivery';
import { ROUTE_HYPOTHESIS, type BrowserResearchEventName, type CaptureVariant, type ResearchReason, type ResearchProperties, type RouteTask } from '../../server/src/research/schema.js';
import { ReplayAdapter } from './research/replay-adapter';

/** The single sender used by the content-free written journey collector. */
export const research = new ResearchDelivery();
export const replay = new ReplayAdapter(research);

let captureRoute = '';
let cancelledAttemptId = '';
/** A first-run onboarding attempt stays open while the learner reaches their first real task. */
let firstRunTaskAttemptId = '';
let writtenEntryAttemptId = '';

/**
 * A first-run attempt starts only while #36's explicit research consent is active. It carries the consented
 * onboarding and optional-example milestones ahead of the capture phases, so the owner timeline can separate
 * onboarding/example time from the first server-confirmed saved action.
 */
export async function beginFirstRunJourney(): Promise<void> {
  if (!research.status.active) await research.start();
  if (!research.status.active) return;
  const current = research.currentAttempt();
  if (current && isFirstRunAttempt(current)) return;
  if (current) return;
  const attempt = research.beginAttempt({
    surface: 'onboarding', entryPoint: 'onboarding', captureVariant: 'written',
  });
  if (!attempt) return;
  firstRunTaskAttemptId = '';
  writtenEntryAttemptId = '';
  preparedAttemptId = '';
  research.emit('first_run.entered');
}

/** First-run landmarks are strictly semantic and carry no learner content. */
export function firstRunMilestone(name: Extract<BrowserResearchEventName,
  'onboarding.entered' | 'onboarding.completed' | 'example.offered' | 'example.started' | 'example.completed' | 'example.skipped'>): void {
  if (isFirstRunAttempt(research.currentAttempt())) research.emit(name);
}

/**
 * A first-run attempt is the open onboarding attempt begun by `beginFirstRunJourney`: it carries the consented
 * `first_run.entered` milestone and has not yet been replaced by an ordinary capture attempt. It is identified by
 * its surface and entry point — fields the server stores — not by a client-only flag, so no reload or second tab
 * can mistake an ordinary capture attempt for the first run.
 */
function isFirstRunAttempt(attempt: ResearchAttempt | null): boolean {
  return !!attempt && attempt.surface === 'onboarding' && attempt.entryPoint === 'onboarding';
}

export async function observeResearchRoute(): Promise<void> {
  const path = location.pathname;
  const hash = location.hash.split('?')[0];
  const key = path === '/record' && hash === '#wanted' ? `${path}${hash}` : path === '/' && hash === '#type' ? hash : '';
  // Ticket 56: a first-run task route (Record, Type or Listen after onboarding/the example) joins the open first-run
  // attempt instead of starting a capture attempt: the task's variant is set, `first_task.entered` fires once, and
  // the capture's `journey.entered` names the route it was reached from.
  const taskRoute = path === '/record' || (path === '/' && ['#record', '#type', '#listen'].includes(hash));
  const current = research.currentAttempt();
  if (taskRoute && current && isFirstRunAttempt(current)) {
    // A recorded first task saves through the same attempt: set the variant before the capture surface prepares it,
    // so the server outcome joins this attempt. Queued events already carry the variant written at emit time; the
    // timeline's first-run and capture phases are keyed to names, not to one variant.
    current.captureVariant = hash === '#wanted' || hash.startsWith('#type') ? 'written' : 'recorded';
    if (firstRunTaskAttemptId !== current.attemptId) {
      firstRunTaskAttemptId = current.attemptId;
      research.emit('first_task.entered');
    }
    if (writtenEntryAttemptId !== current.attemptId) {
      writtenEntryAttemptId = current.attemptId;
      research.emit('journey.entered');
    }
    captureRoute = key || `${path}${hash}`;
    return;
  }
  if (!key) {
    if (captureRoute) cancelWrittenAttempt();
    captureRoute = '';
    return;
  }
  if (captureRoute === key && research.currentAttempt()) return;
  captureRoute = key;
  const existing = research.currentAttempt();
  if (existing?.surface === 'capture' && existing.captureVariant === 'written') return;
  const attempt = research.beginAttempt({
    surface: 'capture', entryPoint: path === '/record' ? 'direct' : 'home', captureVariant: 'written',
  });
  if (attempt) research.emit('journey.entered');
}

function captureEntryPoint(): ResearchAttempt['entryPoint'] {
  return location.pathname === '/record' ? 'direct' : 'home';
}

/** Start one content-free capture attempt when a recording surface is first shown. */
export function prepareCaptureAttempt(variant: CaptureVariant): ResearchAttempt | null {
  let attempt = research.currentAttempt();
  // Ticket 56: the open first-run attempt already carries the onboarding/example milestones; a recorded or Listen
  // first task prepares on it (its variant was set by the task route) instead of starting a capture attempt.
  if (attempt && isFirstRunAttempt(attempt)) {
    if (attempt.captureVariant !== variant && (variant === 'recorded' || variant === 'listen')) attempt.captureVariant = variant;
    else if (attempt.captureVariant !== variant) return null;
    if (attempt && preparedAttemptId !== attempt.attemptId) {
      preparedAttemptId = attempt.attemptId;
      research.emit('journey.prepared');
    }
    return attempt;
  }
  if (!attempt || attempt.captureVariant !== variant) {
    attempt = research.beginAttempt({
      surface: 'capture', entryPoint: captureEntryPoint(), captureVariant: variant,
    });
    if (attempt) research.emitFor(attempt, 'journey.entered');
  }
  if (attempt) research.emitOnce('journey.prepared', 'journey.prepared', {}, attempt);
  return attempt;
}

export const prepareRecordedAttempt = (): ResearchAttempt | null => prepareCaptureAttempt('recorded');
export const prepareListenAttempt = (): ResearchAttempt | null => prepareCaptureAttempt('listen');

export function researchAttemptFor(variant: CaptureVariant): ResearchAttempt | null {
  const current = research.currentAttempt();
  return current?.captureVariant === variant ? current : null;
}

export function recordCaptureEvent(
  eventName: BrowserResearchEventName,
  reason?: ResearchReason,
  attempt: ResearchAttempt | null = research.currentAttempt(),
): void {
  research.emitFor(attempt, eventName, reason ? { reason } : {});
}

export function recordDisplayedBlocker(reason: ResearchReason, attempt: ResearchAttempt | null = research.currentAttempt()): void {
  research.emitOnce(`blocker:${reason}`, 'browser.blocker_displayed', { reason }, attempt);
}

export function recordLocalDraftSaved(reason?: ResearchReason, attempt: ResearchAttempt | null = research.currentAttempt()): void {
  if (!attempt) return;
  recordCaptureEvent('browser.local_draft_saved', reason, attempt);
  research.completeAttempt(attempt);
}

export function cancelCaptureAttempt(variant?: CaptureVariant): void {
  const attempt = research.currentAttempt();
  if (!attempt || (variant && attempt.captureVariant !== variant)) return;
  // Ticket 56: leaving the first task's route before saving records the cancel on the first-run attempt but keeps
  // it open — the learner is still in their first run, and the timeline reports the missing save as incomplete
  // evidence rather than a closed attempt.
  recordCaptureEvent('journey.cancelled', 'user_cancelled', attempt);
  if (isFirstRunAttempt(attempt)) return;
  research.completeAttempt(attempt);
}

export async function startResearch(): Promise<void> {
  await research.start();
  await replay.start();
  await observeResearchRoute();
}

/** The text form is the point at which the written journey has actually rendered. */
let preparedAttemptId = '';
export function prepareWrittenAttempt(): ResearchAttempt | null {
  let attempt = research.currentAttempt();
  // Ticket 56: a written first task prepares on the open first-run attempt (its variant was set by the task route).
  if (attempt && isFirstRunAttempt(attempt)) {
    if (attempt.captureVariant !== 'written') return null;
    if (preparedAttemptId !== attempt.attemptId) {
      preparedAttemptId = attempt.attemptId;
      research.emit('journey.prepared');
    }
    return attempt;
  }
  if (!attempt || attempt.captureVariant !== 'written') {
    attempt = research.beginAttempt({
      surface: 'capture',
      entryPoint: location.pathname === '/record' ? 'direct' : 'home',
      captureVariant: 'written',
    });
    if (attempt) research.emit('journey.entered');
  }
  if (attempt && preparedAttemptId !== attempt.attemptId) {
    preparedAttemptId = attempt.attemptId;
    research.emit('journey.prepared');
  }
  return attempt;
}

export function cancelWrittenAttempt(): void {
  const attempt = research.currentAttempt();
  if (attempt?.captureVariant === 'written' && cancelledAttemptId !== attempt.attemptId) {
    cancelledAttemptId = attempt.attemptId;
    cancelCaptureAttempt('written');
  }
}

let pendingLesson: { momentId: string; attempt: ResearchAttempt } | null = null;

/** Keep the creating capture's safe reference until the corresponding lesson is shown. */
export function expectLessonDisplay(momentId: string, attempt: ResearchAttempt | null | undefined): void {
  pendingLesson = attempt ? { momentId, attempt } : null;
}

export function recordLessonDisplayed(momentId: string): void {
  const pending = pendingLesson;
  if (!pending || pending.momentId !== momentId) return;
  pendingLesson = null;
  research.emitOnce('lesson.displayed', 'browser.lesson_displayed', {}, pending.attempt);
}

export function forgetPendingLesson(): void { pendingLesson = null; }

/** Wraps only the real text-moment request; collection errors never change its result. */
export function createResearchCreation(delivery: ResearchDelivery): <T>(request: () => Promise<T>, attempt?: ResearchAttempt | null) => Promise<T> {
  return async function researchCreation<T>(request: () => Promise<T>, attempt = delivery.currentAttempt()): Promise<T> {
    const context = delivery.captureAttemptContext(attempt);
    if (!context) return request();
    delivery.emitForContext(context, 'journey.submitted');
    try {
      const result = await request();
      delivery.emitForContext(context, 'browser.acknowledged');
      delivery.completeAttempt(context.attempt);
      return result;
    } catch (error) {
      const reason: ResearchReason = typeof navigator !== 'undefined' && navigator.onLine === false
        ? 'server_unavailable' : 'submission_failed';
      delivery.emitForContext(context, 'journey.submission_failed', { reason });
      throw error;
    }
  };
}

export const researchCreation = createResearchCreation(research);

/**
 * Route discovery (ticket 55): one content-free attempt per entry into a named task. The attempt records which task the
 * entry promised, whether that task's screen was shown, whether a wrong turn left before it, and (practice) whether an
 * answer was saved. Observed by the browser; nothing in it carries a moment id, a title, a word or an answer.
 */
export function startRouteTask(task: RouteTask, entryPoint: ResearchAttempt['entryPoint']): ResearchAttempt | null {
  const attempt = research.beginAttempt({ surface: 'practice', entryPoint, captureVariant: 'navigation' });
  if (attempt) research.emitFor(attempt, 'route.task_started', { hypothesis: ROUTE_HYPOTHESIS, task });
  return attempt;
}

export function routeOutcome(
  attempt: ResearchAttempt | null, task: RouteTask, outcome: 'displayed' | 'saved' | 'wrong_turn',
): void {
  if (!attempt) return;
  const props: ResearchProperties = { hypothesis: ROUTE_HYPOTHESIS, task };
  if (outcome === 'wrong_turn') {
    research.emitOnce('route.wrong_turn', 'route.wrong_turn', { ...props, reason: 'left_before_task' }, attempt);
  } else {
    research.emitOnce(`route.${outcome}`, outcome === 'saved' ? 'route.outcome_saved' : 'route.outcome_displayed', props, attempt);
  }
}

/**
 * Raid evidence (#64): the preview demo (`raid_preview`) and a real raid entry (`raid_real`) are separate attempts
 * that the owner's capture totals leave out. Content-free: a reason code at most, never a friend, word or count.
 */
export function beginRaidAttempt(variant: 'raid_preview' | 'raid_real'): ResearchAttempt | null {
  const attempt = research.beginAttempt({ surface: 'raids', entryPoint: 'direct', captureVariant: variant });
  if (attempt && variant === 'raid_preview') research.emitFor(attempt, 'raid.preview_entered');
  return attempt;
}

export function recordRaidEvent(attempt: ResearchAttempt | null, eventName: BrowserResearchEventName, reason?: ResearchReason): void {
  research.emitOnce(`${eventName}:${reason ?? ''}`, eventName, reason ? { reason } : {}, attempt);
}

/** The real-raid attempt begun at entry, still current while its army is loaded and sent. */
export function currentRaidEntryAttempt(): ResearchAttempt | null {
  const current = research.currentAttempt();
  return current?.captureVariant === 'raid_real' ? current : null;
}

/** Map a server refusal code to a readiness reason; anything unlisted is the generic `raid_refused`. */
export function raidRefusalReason(code: unknown): ResearchReason {
  switch (code) {
    case 'raid-not-friends': return 'raid_not_friends';
    case 'raid-pending': return 'raid_pending';
    case 'raid-full': return 'raid_full';
    case 'raid-limit': return 'raid_limit';
    case 'raid-no-town': return 'raid_no_town';
    default: return 'raid_refused';
  }
}
